import { Link, useParams } from "react-router-dom";
import { Camera, Instagram, MapPin } from "lucide-react";
import { catLabel, img } from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import { Eyebrow, MaskLines, Reveal } from "@/components/site/Motion";
import { EventCard } from "@/components/site/EventCard";
import { HireForm } from "@/components/gallery/HireForm";

export default function PhotographerProfile() {
  const { id } = useParams();
  const { data: p, loading, error } = useFetch(`/photographers/${id}`);
  const { data: settings } = useFetch("/settings/public", {});

  if (loading) return <main className="min-h-screen pt-40 text-center text-haze">Carregando…</main>;
  if (error || !p) return <main className="min-h-screen pt-40 text-center" data-testid="photographer-not-found"><p className="font-display text-3xl font-bold">Fotógrafo não encontrado.</p><Link to="/fotografos" className="mt-4 inline-block text-flash">Ver todos</Link></main>;

  const portfolio = Array.isArray(p.portfolio) ? p.portfolio : [];
  const events = Array.isArray(p.events) ? p.events : [];
  return (
    <main data-testid="photographer-profile-page">
      <section className="grain relative overflow-hidden pt-[120px]">
        <div aria-hidden className="pointer-events-none absolute -left-32 top-0 h-[420px] w-[420px] rounded-full bg-violet/30 blur-[140px]" />
        <div className="relative mx-auto grid max-w-[1400px] gap-10 px-5 pb-16 sm:px-8 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <div className="relative overflow-hidden rounded-[1.75rem] border border-white/10">
              {p.has_avatar ? <img src={img("avatar", p.id)} alt={p.name} className="aspect-[4/5] w-full object-cover" /> : <div className="grid aspect-[4/5] place-items-center bg-ink-2"><Camera className="h-12 w-12 text-haze" /></div>}
              <div className="absolute inset-3 vf-corners" />
            </div>
          </Reveal>
          <div className="lg:col-span-8 lg:pl-6">
            <Eyebrow>Fotógrafo associado</Eyebrow>
            <h1 className="mt-5 font-display text-5xl font-extrabold leading-[0.92] tracking-tight sm:text-7xl" data-testid="photographer-name"><MaskLines lines={[p.name]} /></h1>
            <div className="mt-5 flex flex-wrap gap-4 text-haze">
              {p.city && <span className="inline-flex items-center gap-2"><MapPin className="h-4 w-4" />{p.city}</span>}
              {p.instagram && <a href={`https://instagram.com/${p.instagram.replace("@", "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 hover:text-flash" data-testid="photographer-instagram"><Instagram className="h-4 w-4" />{p.instagram}</a>}
            </div>
            <div className="mt-5 flex flex-wrap gap-2">{(p.specialties || []).map((s) => <span key={s} className="rounded-full border border-white/15 px-3 py-1 font-mono text-xs uppercase">{catLabel(s)}</span>)}</div>
            {p.bio && <p className="mt-6 max-w-2xl text-foreground/90 md:text-lg">{p.bio}</p>}
            <div className="mt-10"><HireForm photographer={p} mpEnabled={!!settings?.mp_enabled} /></div>
          </div>
        </div>
      </section>
      {portfolio.length > 0 && (
        <section className="mx-auto max-w-[1400px] px-5 py-16 sm:px-8" data-testid="photographer-portfolio">
          <h2 className="font-display text-3xl font-bold">Portfólio</h2>
          <div className="mt-8 columns-2 gap-3 md:columns-3 lg:columns-4">
            {portfolio.map((ph, i) => (
              <Reveal key={ph.id} delay={(i % 4) * 0.05} className="mb-3 break-inside-avoid overflow-hidden rounded-2xl">
                <img src={img("thumb", ph.id)} alt={ph.code} loading="lazy" className="w-full object-cover transition-transform duration-700 hover:scale-105" style={{ aspectRatio: ph.width && ph.height ? `${ph.width}/${ph.height}` : "4/3" }} />
              </Reveal>
            ))}
          </div>
        </section>
      )}
      {events.length > 0 && (
        <section className="mx-auto max-w-[1400px] px-5 pb-24 sm:px-8">
          <h2 className="font-display text-3xl font-bold">Eventos fotografados</h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{events.map((ev) => <EventCard key={ev.id} ev={ev} />)}</div>
        </section>
      )}
    </main>
  );
}
